import React, { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const FAQS = [
  {
    q: 'How do search credits work?',
    a: 'One credit equals one company search. You get every contact we find for that company, no matter how many, for that single credit.',
  },
  {
    q: 'Is this a database or a live search?',
    a: 'It is a live search every time. When you search, we run a fresh real-time scan and extract the results on the spot. Data is automatically deleted after 30 days.',
  },
  {
    q: 'What exactly do I get per search?',
    a: 'Full name and job title for every decision maker we find at that address. Coming soon: verified work email, verified mobile number, best time to call, and AI-written opening lines.',
  },
  {
    q: 'Can I upload a list of companies?',
    a: 'Yes. The bulk CSV upload feature lets you upload hundreds or thousands of companies at once. Results are returned as a single clean export file. This is live today.',
  },
  {
    q: 'How does the 30-day data deletion work?',
    a: 'All contact data we find is automatically purged from our systems after 30 days. This keeps us GDPR clean and ensures you are always working with fresh data on your next search.',
  },
 
];

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const { ref, inView } = useInView(0.1);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="bg-slate-50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-14 lg:gap-20">

          {/* left */}
          <div
            className="lg:col-span-1"
            style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-3">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-[-0.03em] text-slate-900 leading-tight mb-5">
              Frequently asked questions
            </h2>
            <p className="text-[14.5px] text-slate-500 leading-relaxed mb-6">
              Can't find what you're looking for? Reach out and we'll get back to you.
            </p>
            <a
              href="mailto:support@localcontact.ai"
              className="inline-flex items-center gap-2 text-[13.5px] font-bold text-[#1B3A8A] hover:text-slate-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact support
            </a>
          </div>

          {/* right: accordion */}
          <div className="lg:col-span-2 space-y-2">
            {FAQS.map((faq, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className={`rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? 'border-[#1B3A8A] bg-white shadow-lg shadow-[#1B3A8A]/8'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                  style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0)' : 'translateY(16px)',
                    transition: `opacity 0.5s ease ${i * 60}ms, transform 0.5s ease ${i * 60}ms, border-color 0.2s ease`,
                  }}
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-5 text-left group cursor-pointer"
                    onClick={() => setOpen(isOpen ? null : i)}
                  >
                    <span className={`text-[15px] font-bold pr-8 transition-colors duration-150 ${isOpen ? 'text-slate-900' : 'text-slate-700 group-hover:text-slate-900'}`}>
                      {faq.q}
                    </span>
                    <span className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border-2 transition-all duration-200 ${
                      isOpen
                        ? 'bg-[#1B3A8A] border-[#1B3A8A] text-white'
                        : 'border-slate-200 text-slate-500 group-hover:border-slate-400'
                    }`}>
                      <svg width="10" height="10" viewBox="0 0 8 8" fill="none">
                        {isOpen
                          ? <line x1="1" y1="4" x2="7" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          : <>
                              <line x1="4" y1="1" x2="4" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <line x1="1" y1="4" x2="7" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </>
                        }
                      </svg>
                    </span>
                  </button>
                  <div style={{ maxHeight: isOpen ? '400px' : '0', overflow: 'hidden', transition: 'max-height 0.35s ease' }}>
                    <p className="px-6 pb-6 text-[14px] text-slate-600 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
}
